import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks';
import { useDocumentTitle } from '../hooks/core';
import { referralApi } from '../api';
import type { SSOProvider } from '../api/auth';
import { SSOProviderButtons } from '../components/auth';

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isLoading, error, getSSOProviders, loginWithSSO } = useAuth();

  useDocumentTitle('Create Account');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState<boolean | null>(null);
  const [checkingReferral, setCheckingReferral] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<SSOProvider[]>([]);
  const [ssoLoading, setSsoLoading] = useState(false);

  // Fetch SSO providers on mount
  useEffect(() => {
    getSSOProviders()
      .then(setSsoProviders)
      .catch(() => {
        // Ignore errors - SSO just won't be shown
      });
  }, [getSSOProviders]);

  const handleSSOSignup = async (providerId: string) => {
    setSsoLoading(true);
    try {
      await loginWithSSO(providerId, redirectTo);
    } catch {
      setSsoLoading(false);
    }
  };

  // Get redirect destination from query params (default to /boards)
  const redirectTo = searchParams.get('redirectTo') || '/boards';

  // Check for referral code in URL params
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      // Validate the referral code
      setCheckingReferral(true);
      referralApi.validateCode(refCode)
        .then(result => setReferralValid(result.valid))
        .catch(() => setReferralValid(false))
        .finally(() => setCheckingReferral(false));
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tosAccepted) {
      return;
    }
    try {
      await register({
        email,
        password,
        displayName,
        referralCode: referralValid ? referralCode : undefined,
        tosAcceptedAt: new Date().toISOString(),
      });
      navigate(redirectTo);
    } catch {
      // Error is handled by useAuth
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Create Account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} data-testid="register-form">
            <TextField
              fullWidth
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              margin="normal"
              required
              autoComplete="name"
              inputProps={{ 'data-testid': 'register-display-name-input' }}
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              inputProps={{ 'data-testid': 'register-email-input' }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              inputProps={{ 'data-testid': 'register-password-input' }}
            />

            {referralCode && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Referred by:
                </Typography>
                <Chip
                  label={referralCode}
                  size="small"
                  color={
                    checkingReferral
                      ? 'default'
                      : referralValid
                        ? 'success'
                        : 'error'
                  }
                  variant={referralValid ? 'filled' : 'outlined'}
                />
                {checkingReferral && (
                  <Typography variant="caption" color="text.secondary">
                    Validating...
                  </Typography>
                )}
                {referralValid === false && (
                  <Typography variant="caption" color="error">
                    Invalid code
                  </Typography>
                )}
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  color="primary"
                  data-testid="register-tos-checkbox"
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link component={RouterLink} to="/terms" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link component={RouterLink} to="/privacy" target="_blank">
                    Privacy Policy
                  </Link>
                </Typography>
              }
              sx={{ mt: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2 }}
              disabled={isLoading || !tosAccepted}
              data-testid="register-submit-button"
            >
              {isLoading ? 'Creating Account...' : 'Register'}
            </Button>

            <Typography variant="body2" align="center">
              Already have an account?{' '}
              <Link component={RouterLink} to={`/login${redirectTo !== '/boards' ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`} data-testid="register-login-link">
                Login
              </Link>
            </Typography>
          </Box>

          {/* SSO Providers */}
          <SSOProviderButtons
            providers={ssoProviders}
            onSelect={handleSSOSignup}
            disabled={isLoading || ssoLoading}
            testIdPrefix="register-sso"
          />
          {ssoProviders.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              By continuing, you agree to our{' '}
              <Link component={RouterLink} to="/terms" target="_blank">Terms</Link>
              {' '}and{' '}
              <Link component={RouterLink} to="/privacy" target="_blank">Privacy Policy</Link>
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
